import React, { useEffect, useLayoutEffect, useState } from 'react'
import { Redirect, useLocation, useHistory } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import moment from 'moment-timezone'
import classNames from 'classnames'
import {
  useErrorService,
  surveyType,
  ProsperityComparisonReport,
  SurveyComparisonReport,
  PrintableArea,
} from '@abroad/components'
import API from '../utils/API'
import { scrollAdminContentToTop } from '../utils/utility'

const orderComparisonData = (data, surveyIds) => {
  if (!data?.length) {
    return []
  }
  const ordered = surveyIds
    .map((surveyId) =>
      data.find((item) => item?._id === surveyId || item?.id === surveyId),
    )
    .filter(Boolean)
  if (ordered.length === 2) {
    return ordered
  }
  return [...data].sort(
    (a, b) =>
      moment(a?.submittedAt).valueOf() - moment(b?.submittedAt).valueOf(),
  )
}

const resolveClientId = (search, pathname) => {
  const query = new URLSearchParams(search)
  const fromQuery = query.get('clientId') || query.get('clientid')
  if (fromQuery) {
    return fromQuery
  }
  const legacyMatch = pathname.match(
    /\/(?:prosperity|survey)\/client\/([^/]+)\/comparison-report/,
  )
  return legacyMatch?.[1] || null
}

export const ClientAssessmentComparison = () => {
  const { search, pathname } = useLocation()
  const history = useHistory()
  const Error = useErrorService()
  const [isLoading, setIsLoading] = useState(true)
  const [comparisonData, setComparisonData] = useState([])
  const query = new URLSearchParams(search)
  const surveyIds = query.getAll('survey')
  const clientId = resolveClientId(search, pathname)
  const isProsperityCompare =
    (query.get('surveyType') || surveyType.AWARENESS) ===
      surveyType.PROSPERITY || pathname.includes('/prosperity')
  const querySurveyType = isProsperityCompare
    ? surveyType.PROSPERITY
    : surveyType.AWARENESS

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
    scrollAdminContentToTop()

    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto'
      }
    }
  }, [pathname, search])

  const hasComparisonData = comparisonData.length >= 2
  const isValidComparisonRoute = surveyIds.length === 2 && Boolean(clientId)

  useLayoutEffect(() => {
    if (!isValidComparisonRoute || isLoading) {
      return
    }
    scrollAdminContentToTop()
    const frameId = requestAnimationFrame(scrollAdminContentToTop)
    const timeoutId = setTimeout(scrollAdminContentToTop, 100)
    return () => {
      cancelAnimationFrame(frameId)
      clearTimeout(timeoutId)
    }
  }, [isLoading, hasComparisonData, isValidComparisonRoute])

  useEffect(() => {
    if (!isValidComparisonRoute) {
      return
    }

    const getComparison = async () => {
      setIsLoading(true)
      try {
        const { data } = await API.CoachAPI.coachClient.getUserComparisonReport(
          clientId,
          { surveyIds },
          querySurveyType,
        )
        setComparisonData(orderComparisonData(data, surveyIds))
        setIsLoading(false)
      } catch (e) {
        if (
          e?.code === 'ValidationError' ||
          e?.code === 'resource_not_found' ||
          e?.code === 'not_found'
        ) {
          history.replace('/404')
        } else {
          setIsLoading(false)
          Error.showError(e)
        }
      }
    }

    getComparison()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, querySurveyType, clientId])

  if (!isValidComparisonRoute) {
    return <Redirect to='/admin/home' />
  }

  const handleGoBack = () => {
    if (history.length > 1) {
      history.goBack()
    } else if (clientId) {
      history.push(`/admin/clients/${clientId}`)
    } else {
      history.push('/admin/home')
    }
  }

  const compareHeaderTitle = isProsperityCompare
    ? 'True Prosperity Assessment'
    : 'Awareness Assessment'

  const compareHeaderProps = {
    showClientCompareHeader: true,
    compareHeaderTitle,
    onGoBack: handleGoBack,
    isRenderFromOP: false,
  }

  const comparisonReportProps = {
    preDate: hasComparisonData
      ? moment(comparisonData[0]?.submittedAt).format('MMM DD, YYYY')
      : '',
    postDate: hasComparisonData
      ? moment(comparisonData[1]?.submittedAt).format('MMM DD, YYYY')
      : '',
    isLoading: isLoading || !hasComparisonData,
    comparisonData: hasComparisonData ? comparisonData : [],
    isRequiredToFormatDate: false,
    ...compareHeaderProps,
  }

  const ComparisonReport = isProsperityCompare
    ? ProsperityComparisonReport
    : SurveyComparisonReport

  return (
    <>
      <Helmet>
        <title>Compare Results | Abroad</title>
      </Helmet>
      <main
        className={classNames(
          'survey-comparison survey-category-page abroad-fs-exclude',
          {
            'survey-comparison-awareness': !isProsperityCompare,
            'wisdom-assessment': isProsperityCompare,
          },
        )}>
        <div className='custom-offset-lg-1 custom-col-lg-12 px-lg-0'>
          <ComparisonReport {...comparisonReportProps} />
        </div>
      </main>
      <PrintableArea printSelector='main' />
    </>
  )
}

export default ClientAssessmentComparison
